import { useT } from "@repo/transifex";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { ReportTableSection } from "~/components/report";
import { useVatLabel } from "~/hooks/use-vat-label";
import { priceFormatter } from "~/utils/price-formatter";

import { type DepositFeeSummaryData } from "./types";

type DepositFeeSummarySectionProps = {
  data: DepositFeeSummaryData;
};

/** Shown in place of an amount that is absent or not applicable, so it doesn't read as zero. */
const EMPTY_AMOUNT = "—";

export const DepositFeeSummarySection = ({
  data
}: DepositFeeSummarySectionProps) => {
  const t = useT();
  const { locale } = useCurrentTillLocalisation();
  const { vatLabel } = useVatLabel();

  // Without decimals a 300 total would read "£3" next to "£1.50" rows.
  const fmt = (cents: number) =>
    priceFormatter({
      value: cents,
      currency: data.currency,
      locale,
      options: { showDecimals: true }
    });

  const columns = [
    {
      key: "schemeName",
      label: t("Deposit Fee Scheme"),
      align: "left" as const,
      width: "w-2/5"
    },
    {
      key: "feeExclVat",
      label: t("Value"),
      align: "right" as const,
      width: "w-1/5"
    },
    {
      key: "netQuantity",
      label: t("Net Qty."),
      align: "right" as const,
      width: "w-1/5"
    },
    {
      key: "netAmount",
      label: t("Net Amount"),
      align: "right" as const,
      width: "w-1/5"
    }
  ];

  const rows = data.rows.map((row) => ({
    id: row.id,
    schemeName: row.schemeName,
    feeExclVat: fmt(row.feeExclVat),
    netQuantity: row.netQuantity,
    netAmount: fmt(row.netAmount)
  }));

  const footer = {
    schemeName: t("TOTAL"),
    feeExclVat: EMPTY_AMOUNT,
    netQuantity: data.totals.netQuantity,
    netAmount: fmt(data.totals.netAmount)
  };

  return (
    <ReportTableSection
      title={t("Deposit Fee Summary (Excl. {vatLabel})", { vatLabel })}
      columns={columns}
      rows={rows}
      rowKey="id"
      footer={footer}
    />
  );
};
